/* eslint-disable @typescript-eslint/consistent-type-imports */
/* eslint-disable @typescript-eslint/consistent-type-imports */
import React from "react";
import { motion, useCycle } from "framer-motion";
import { Input, Loader, Navbar } from "components";
import { SvgTesMessageSquareEdit, TesCheckedboxMarkedCircle } from "components/icons";
import { user } from "assets/images";
import { Link } from "react-router-dom";
import {
  UPDATE_PROFILE,
  SCHOOL,
  USER,
  PRINCIPALSANDVICEPRINCIPALS,
  UPDATESCHOOL
} from "routes/CONSTANTS";
import { UserDetails } from "types";
import { Formik } from "formik";
import LogoLoader from "components/widgets/loader/LogoLoader";

import Sidebar, { SidebarItem } from "components/widgets/sidebar/sideBar";
import { FaHome, FaUsers, FaSchool, FaFileAlt, FaEdit, FaExchangeAlt } from "react-icons/fa";
import { useAppSelector } from "hooks";
interface Props {
  loading: boolean;
  userDetails: UserDetails;
  image: string;
  pictureUpload: (event: React.ChangeEvent<HTMLInputElement>) => void | Promise<void>;
}

const cardStyle =
  "p-4 bg-green-800/90 border border-black/40 rounded-xl shadow-lg hover:shadow-xl transition-all";

// Chrome on Android (and most mobile webviews) doesn't reliably render PDFs
// inside an <iframe>, so route mobile through Google's viewer which renders
// server-side and just streams the result back in.
const isMobileDevice = (): boolean =>
  typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const getPreviewUrl = (url: string): string =>
  isMobileDevice()
    ? `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
    : url;

function ProfileView({ loading, userDetails, pictureUpload, image }: Props) {
  const [sidebarOpen, toggleSidebar] = useCycle(true, false);

  const isZonalCoordinatingPrincipal = userDetails?.position === "Zonal Co-ordinating Principal";

  const isPrincipalOrVicePrincipal =
    userDetails?.position === "Principal" || userDetails?.position === "Vice-Principal";

  const letterUrl = isZonalCoordinatingPrincipal
    ? userDetails.letters.zonalCoordinatingPrincipalLetter
    : isPrincipalOrVicePrincipal
      ? userDetails.letters.postingLetter
      : "";

  const previewUrl = letterUrl ? getPreviewUrl(letterUrl) : undefined;

  const menuItems: SidebarItem[] = [
    { label: "Dashboard", path: "#", icon: <FaHome /> },
    { label: "Users", path: USER, icon: <FaUsers />, adminOnly: true },
    { label: "Schools", path: SCHOOL, icon: <FaSchool />, adminOnly: true },

    {
      label: "Principals and Vice Principals Posting",
      path: PRINCIPALSANDVICEPRINCIPALS,
      icon: <FaExchangeAlt />,
      adminOnly: true
    },
    { label: "Update Schools", path: UPDATESCHOOL, icon: <FaEdit />, adminOnly: true },
    {
      label: "Letters",
      path: letterUrl ?? "#",
      icon: <FaFileAlt />
    }

    // { label: "Settings", path: "#", icon: <FaCog /> }
  ];

  const { isAdmin } = useAppSelector((state) => state.auth);

  return (
    <div className="relative min-h-screen bg-black text-white">
      {/* Sidebar */}
      <Sidebar
        items={menuItems}
        isAdmin={isAdmin ?? false}
        userRole={userDetails?.staffType ?? ""}
        isOpen={sidebarOpen}
        toggle={() => toggleSidebar()}
      />

      {/* Main Content */}
      <div
        className={`min-h-screen bg-black text-white transition-all duration-300 ${
          sidebarOpen ? "ml-32" : "ml-0"
        }`}
      >
        {/* Hamburger toggle button */}
        <button
          onClick={() => toggleSidebar()}
          className="fixed top-4 left-4 z-50 p-2 bg-green-800 text-yellow-400 rounded-lg hover:bg-green-700 transition shadow-lg"
        >
          <span className="text-2xl">{sidebarOpen ? "✕" : "☰"}</span>
        </button>

        {/* Pass sidebar state to Navbar */}
        <Navbar isSidebarOpen={sidebarOpen} hasSidebar={true} />

        {loading ? (
          <LogoLoader />
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-7xl mx-auto p-6 pt-20"
          >
            {/* PROFILE IMAGE */}
            <Formik initialValues={{ file: null }} onSubmit={() => undefined}>
              <div className="flex justify-center mb-10">
                <div className="relative w-36 h-36">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="relative w-full h-full rounded-full overflow-hidden ring-4 ring-yellow-500/40 shadow-xl"
                  >
                    <img
                      src={image || user}
                      alt="Profile"
                      className="w-full h-full object-cover rounded-full"
                    />

                    <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition flex items-center justify-center">
                      <span className="text-yellow-300 text-sm">Update Photo</span>
                    </div>

                    <div className="absolute bottom-1 right-1 bg-green-700 p-1 rounded-full">
                      <TesCheckedboxMarkedCircle color="yellow" width="22px" height="22px" />
                    </div>
                  </motion.div>

                  <Input
                    type="file"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={pictureUpload}
                    accept="image/png, image/jpeg, image/jpg"
                  />

                  <label className="flex items-center justify-center gap-2 mt-3 text-yellow-400 cursor-pointer">
                    <SvgTesMessageSquareEdit color="yellow" />
                    Change Photo
                  </label>
                </div>
              </div>
            </Formik>

            {/* LETTER (posting letter for Principal/Vice-Principal, or zonal
                letter for a Zonal Co-ordinating Principal) - previewed
                directly inline instead of behind a click/modal */}
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="mb-8 p-4 bg-gradient-to-br from-green-800 to-black border border-yellow-500/30 rounded-xl shadow-xl"
            >
              <p className="text-yellow-400 font-semibold mb-3 text-center">Posting Letter</p>
              {letterUrl ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-full h-96 rounded-lg overflow-hidden bg-white shadow-inner">
                    <iframe
                      key={previewUrl}
                      src={previewUrl}
                      title="Letter preview"
                      className="w-full h-full border-0"
                    />
                  </div>

                  <a
                    href={letterUrl}
                    download
                    className="text-sm bg-yellow-500 text-black font-semibold px-4 py-2 rounded-md hover:bg-yellow-400 transition"
                  >
                    Download Letter
                  </a>
                </div>
              ) : (
                <span className="text-yellow-300 block text-center">No letter available yet</span>
              )}
            </motion.div>

            {/* BASIC INFO */}
            <motion.div whileHover={{ scale: 1.01 }} className={`${cardStyle} mb-6`}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-yellow-400 uppercase">Full Name</p>
                  <p className="text-lg">{userDetails?.staffName || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-yellow-400 uppercase">Grade Level</p>
                  <p className="text-lg">{userDetails?.gradeLevel || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-yellow-400 uppercase">Staff Type</p>
                  <p className="text-lg">{userDetails?.staffType || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-yellow-400 uppercase">OG Number</p>
                  <p className="text-lg">{userDetails?.ogNumber || "-"}</p>
                </div>
              </div>
            </motion.div>

            {/* STAFF DETAILS */}
            <motion.div className={`${cardStyle} mb-6`}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-yellow-400">File Number</p>
                  <p className="text-lg">{userDetails?.tscFileNumber || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-yellow-400">Staff Type</p>
                  <p className="text-lg">{userDetails?.staffType || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-yellow-400">Cadre</p>
                  <p className="text-lg">{userDetails?.cadre || "-"}</p>
                </div>
              </div>
            </motion.div>

            {/* CONTACT */}
            <motion.div className={`${cardStyle} mb-6`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-yellow-400">Phone Number</p>
                  <p className="text-lg">{userDetails?.phoneNumber || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-yellow-400">Nationality</p>
                  <p className="text-lg">{userDetails?.nationality || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-yellow-400">State of Origin</p>
                  <p className="text-lg">{userDetails?.stateOfOrigin || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-yellow-400">Local Government</p>
                  <p className="text-lg">{userDetails?.lgOfOrigin || "-"}</p>
                </div>
              </div>
            </motion.div>

            {/* SUBJECTS */}
            <motion.div className={`${cardStyle} mb-6`}>
              <p className="text-yellow-400 font-semibold mb-4">Subjects Assigned</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {userDetails?.subjectsTaught?.length ? (
                  userDetails.subjectsTaught.map((subject, i) => (
                    <motion.div
                      key={i}
                      whileHover={{ y: -4 }}
                      className="p-4 bg-green-900 border border-black/40 rounded-lg shadow"
                    >
                      <span className="text-yellow-300 font-semibold">Subject:</span> {subject}
                    </motion.div>
                  ))
                ) : (
                  <span className="text-yellow-300">No subject assigned available</span>
                )}
              </div>
            </motion.div>

            {/* EMAIL & ADDRESS */}
            <motion.div className={`${cardStyle} mb-6`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-yellow-400">Email Address</p>
                  <p className="text-lg">{userDetails?.email || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-yellow-400">Address</p>
                  <p className="text-lg">{userDetails?.residentialAddress || "-"}</p>
                </div>
              </div>
            </motion.div>

            {/* QUALIFICATIONS */}
            <motion.div className={`${cardStyle} mb-6`}>
              <p className="text-yellow-400 font-semibold mb-4">All Schools Attended Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {userDetails?.qualifications?.length ? (
                  userDetails.qualifications.map((q, i) => (
                    <motion.div
                      key={i}
                      whileHover={{ y: -4 }}
                      className="p-4 bg-green-900 border border-black/40 rounded-lg shadow"
                    >
                      <p>
                        <span className="text-yellow-300">Institution:</span> {q.schoolName}
                      </p>
                      <p>
                        <span className="text-yellow-300">Specialization:</span> {q.specialization}
                      </p>
                      <p>
                        <span className="text-yellow-300">Degree:</span> {q.degreeType}
                      </p>
                      <p>
                        <span className="text-yellow-300">Years:</span> {q.startYear} - {q.endYear}
                      </p>
                    </motion.div>
                  ))
                ) : (
                  <span className="text-yellow-300">No school records available</span>
                )}
              </div>
            </motion.div>

            {/* PENSION DETAILS */}
            <motion.div className={`${cardStyle} mb-8`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-yellow-400">PFA</p>
                  <p className="text-lg">{userDetails?.pfa ?? "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-yellow-400">Pension Number</p>
                  <p className="text-lg">{userDetails?.pensionNumber ?? "-"}</p>
                </div>
              </div>
            </motion.div>

            {/* NEXT OF KIN */}
            <motion.div className={`${cardStyle} mb-8`}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-yellow-400">Next of Kin Name</p>
                  <p className="text-lg">{userDetails?.nameOfNextOfKin ?? "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-yellow-400">Next of Kin Address</p>
                  <p className="text-lg">{userDetails?.nextOfKinAddress ?? "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-yellow-400">Next of Kin Phone</p>
                  <p className="text-lg">{userDetails?.nextOfKinPhoneNumber ?? "-"}</p>
                </div>
              </div>
            </motion.div>

            {/* EDIT BUTTON */}
            <div className="flex justify-end">
              <motion.div whileTap={{ scale: 0.95 }}>
                <Link
                  to={UPDATE_PROFILE}
                  className="bg-yellow-500 text-black font-semibold py-2 px-6 rounded-lg shadow-lg hover:bg-yellow-400 transition"
                >
                  {loading ? <Loader /> : "Edit Profile"}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default ProfileView;
